import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./DocumentVerification.scss";
import Logo from "../../iamges/logo.png";
import { clearSession, getSession, initials } from "./auth";
import {
  PastVerificationRecord,
  fetchPastVerifications,
  deleteDocumentVerification,
  countVerificationDocuments,
} from "../../services/documentVerification.service";

function formatGeneratedDate(dateStr: string) {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { date: dateStr, time: "" };
    const dateFormatted = d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const timeFormatted = d
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    return { date: dateFormatted, time: timeFormatted };
  } catch {
    return { date: dateStr, time: "" };
  }
}

const toArray = (value: any): any[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const asText = (value: any, fallback = ""): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => asText(item)).filter(Boolean).join(" · ");
  if (typeof value === "object") {
    return (
      value.label ||
      value.summary ||
      value.finding ||
      value.evidence ||
      value.reason ||
      value.risk ||
      value.pattern ||
      value.document ||
      JSON.stringify(value)
    );
  }
  return fallback;
};

const uniqueNonEmpty = (items: string[]) =>
  Array.from(new Set(items.map((item) => item.trim()).filter(Boolean)));

const buildAiPreview = (record: PastVerificationRecord) => {
  const ai = record.aiResult;

  if (record.aiError) {
    return {
      state: "failed" as const,
      title: "AI generated data failed",
      summary: record.aiError,
      decision: "Failed",
      confidence: "—",
      verified: [] as string[],
      flagged: [record.aiError],
    };
  }

  if (!ai || ai.skipped) {
    return {
      state: "pending" as const,
      title: "AI generated data pending",
      summary: ai?.error || "The uploaded documents are saved. AI review has not finished yet.",
      decision: "Pending",
      confidence: "—",
      verified: [] as string[],
      flagged: [] as string[],
    };
  }

  const verified = uniqueNonEmpty([
    ...toArray(ai.verifiedClearly).map((item) => asText(item)),
    ...toArray(ai.identity?.findings).slice(0, 2).map((item) => asText(item)),
    ...toArray(ai.legalPosition).slice(0, 1).map((item) => asText(item)),
  ]).slice(0, 4);

  const flagged = uniqueNonEmpty([
    ...toArray(ai.flaggedOrCouldNotVerify).map((item) => asText(item)),
    ...toArray(ai.inconsistencies).map((item) => asText(item)),
    ...toArray(ai.missingDocuments).map((item) => `Missing: ${asText(item)}`),
    ...toArray(ai.identity?.redFlags).map((item) => asText(item)),
    ...toArray(ai.riskRanking).map((item) => asText(item.risk || item)),
  ]).slice(0, 5);

  const confidence =
    ai.confidenceScore === 0 || ai.confidenceScore
      ? `${Math.round(Number(ai.confidenceScore) * (Number(ai.confidenceScore) <= 1 ? 100 : 1))}%`
      : "Not returned";

  return {
    state: "ready" as const,
    title: "AI generated data",
    summary: asText(ai.summary, "AI summary was generated, but no summary text was returned."),
    decision: asText(ai.decision, record.status),
    confidence,
    verified,
    flagged,
  };
};

const DocumentVerificationPast: React.FC = () => {
  const navigate = useNavigate();
  const session = getSession();
  const displayName = session?.fullName || session?.email || "Account";

  const [verifications, setVerifications] = useState<PastVerificationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search & Sort filters
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  // Delete modal state
  const [selectedForDelete, setSelectedForDelete] =
    useState<PastVerificationRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Polling ref for checking status
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      setError(null);
      const data = await fetchPastVerifications();
      setVerifications(data);
    } catch (err: any) {
      if (!isBackground) {
        setError(err?.message || "Failed to load verifications.");
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Live auto-polling: If any verification is in progress ("checking", "uploaded", "uploading"),
  // poll every 6 seconds so the UI updates as soon as the background AI finishes and sends email.
  useEffect(() => {
    const hasPending = verifications.some((v) =>
      ["checking", "uploaded", "uploading"].includes(v.status)
    );

    if (hasPending) {
      pollTimerRef.current = setTimeout(() => {
        loadData(true);
      }, 6000);
    }

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [verifications]);

  const handleLogout = () => {
    clearSession();
    navigate("/document-verification/login", { replace: true });
  };

  const handleOpenSummary = (verificationId: string) => {
    navigate(`/document-verification?verificationId=${encodeURIComponent(verificationId)}`);
  };

  const handleDownloadPdf = (verificationId: string) => {
    navigate(
      `/document-verification?verificationId=${encodeURIComponent(
        verificationId
      )}&print=true`
    );
  };

  const handleConfirmDelete = async () => {
    if (!selectedForDelete) return;
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await deleteDocumentVerification(selectedForDelete.verificationId);
      setVerifications((prev) =>
        prev.filter((v) => v.verificationId !== selectedForDelete.verificationId)
      );
      setSelectedForDelete(null);
    } catch (err: any) {
      setDeleteError(err?.message || "Could not delete verification.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Filtered and sorted verifications
  const filteredVerifications = useMemo(() => {
    let list = [...verifications];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (v) =>
          (v.applicantName && v.applicantName.toLowerCase().includes(q)) ||
          (v.province && v.province.toLowerCase().includes(q)) ||
          (v.verificationId && v.verificationId.toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      return sortOrder === "newest" ? timeB - timeA : timeA - timeB;
    });

    return list;
  }, [verifications, searchQuery, sortOrder]);

  return (
    <div className="dv-root">
      {/* Header */}
      <header className="dv-header">
        <div className="dv-brand" onClick={() => navigate("/document-verification")} style={{ cursor: "pointer" }}>
          <img src={Logo} alt="HomeOwner" />
          <div>
            <div className="dv-brand__title">HomeOwner</div>
            <div className="dv-brand__sub">Document verification</div>
          </div>
        </div>
        <nav className="dv-nav">
          <button
            type="button"
            className="dv-nav-pill"
            onClick={() => navigate("/document-verification")}
          >
            <i className="ph ph-shield-check" />
            New verification
          </button>
          <button type="button" className="dv-nav-pill dv-nav-pill--active">
            <i className="ph ph-clock-counter-clockwise" />
            Past verifications
          </button>
        </nav>
        <div className="dv-user">
          <div className="dv-avatar">{initials(displayName)}</div>
          <span className="dv-user__name" style={{ fontWeight: 600, fontSize: 13 }}>
            {displayName}
          </span>
        </div>
        <button type="button" className="dv-logout-btn" onClick={handleLogout}>
          <i className="ph ph-sign-out" />
          <span className="dv-logout-btn__label">Log out</span>
        </button>
      </header>

      {/* Main Body */}
      <div className="dv-body dv-past-body">
        {/* Title & Actions Row */}
        <div className="dv-past-heading-row">
          <div className="dv-past-heading-left">
            <h1 className="dv-h1">Past verifications</h1>
            <p className="dv-past-subtitle">
              Every summary you have generated, saved with its PDF so you can open or download it
              later. Only you can see these.
            </p>
          </div>
          <div className="dv-past-heading-right">
            <button
              type="button"
              className="dv-btn dv-btn--primary"
              onClick={() => navigate("/document-verification")}
            >
              <i className="ph ph-plus" />
              New verification
            </button>
            <span className="dv-past-count-badge">
              {verifications.length} {verifications.length === 1 ? "verification" : "verifications"}
            </span>
          </div>
        </div>

        {/* Filters Bar: Search & Sort */}
        <div className="dv-past-filters-bar">
          <div className="dv-past-search-box">
            <i className="ph ph-magnifying-glass dv-past-search-icon" />
            <input
              type="text"
              className="dv-past-search-input"
              placeholder="Search by applicant name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="dv-past-search-clear"
                onClick={() => setSearchQuery("")}
              >
                <i className="ph ph-x" />
              </button>
            )}
          </div>

          <div className="dv-past-sort-group">
            <button
              type="button"
              className={`dv-past-sort-btn ${sortOrder === "newest" ? "dv-past-sort-btn--active" : ""}`}
              onClick={() => setSortOrder("newest")}
            >
              Newest first
            </button>
            <button
              type="button"
              className={`dv-past-sort-btn ${sortOrder === "oldest" ? "dv-past-sort-btn--active" : ""}`}
              onClick={() => setSortOrder("oldest")}
            >
              Oldest first
            </button>
          </div>
        </div>

        {/* Verifications Table Card */}
        <div className="dv-card dv-past-table-card">
          {loading ? (
            <div className="dv-past-loading-state">
              <i className="ph ph-circle-notch dv-spin" />
              <span>Loading your past verifications...</span>
            </div>
          ) : error ? (
            <div className="dv-past-error-state">
              <i className="ph ph-warning-circle" />
              <span>{error}</span>
              <button type="button" className="dv-btn dv-btn--outline" onClick={() => loadData()}>
                Try again
              </button>
            </div>
          ) : filteredVerifications.length === 0 ? (
            <div className="dv-past-empty-state">
              <div className="dv-past-empty-icon">
                <i className="ph ph-files" />
              </div>
              <h3>
                {searchQuery ? "No matching verifications found" : "No verifications yet"}
              </h3>
              <p>
                {searchQuery
                  ? `No past verification found matching "${searchQuery}".`
                  : "When you run document verification checks on prospective tenants, their summaries and records will appear here."}
              </p>
              {!searchQuery && (
                <button
                  type="button"
                  className="dv-btn dv-btn--primary"
                  onClick={() => navigate("/document-verification")}
                >
                  <i className="ph ph-shield-check" />
                  Start first verification
                </button>
              )}
            </div>
          ) : (
            <div className="dv-past-table-wrap">
              <table className="dv-past-table">
                <thead>
                  <tr>
                    <th>APPLICANT (LABEL)</th>
                    <th>PROPERTY PROVINCE</th>
                    <th>GENERATED</th>
                    <th>DOCUMENTS</th>
                    <th>STATUS</th>
                    <th style={{ textAlign: "right" }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVerifications.map((v) => {
                    const isChecking = ["checking", "uploaded", "uploading"].includes(v.status);
                    const isFailed = v.status === "failed";
                    const isReady = ["completed", "needs_review"].includes(v.status);
                    const docCount = countVerificationDocuments(v.documents);
                    const genDate = formatGeneratedDate(v.aiProcessedAt || v.updatedAt || v.createdAt);
                    const aiPreview = buildAiPreview(v);

                    return (
                      <React.Fragment key={v.verificationId}>
                        <tr className="dv-past-row">
                          {/* APPLICANT */}
                          <td className="dv-past-cell-applicant">
                            <span className="dv-past-folder-icon">
                              <i className="ph ph-folder-simple" />
                            </span>
                            <span className="dv-past-applicant-name">
                              {v.applicantName || "Unnamed Applicant"}
                            </span>
                          </td>

                          {/* PROPERTY PROVINCE */}
                          <td className="dv-past-cell-province">
                            {v.province || "—"}
                          </td>

                          {/* GENERATED */}
                          <td className="dv-past-cell-generated">
                            <div className="dv-past-date-primary">{genDate.date}</div>
                            {genDate.time && (
                              <div className="dv-past-date-sub">{genDate.time}</div>
                            )}
                          </td>

                          {/* DOCUMENTS */}
                          <td className="dv-past-cell-docs">
                            {docCount} {docCount === 1 ? "file" : "files"}
                          </td>

                          {/* STATUS */}
                          <td className="dv-past-cell-status">
                            {isChecking && (
                              <span className="dv-past-status dv-past-status--checking">
                                <i className="ph ph-circle-notch dv-spin" />
                                Checking
                              </span>
                            )}
                            {isReady && (
                              <span className="dv-past-status dv-past-status--ready">
                                <i className="ph ph-check-circle-fill" />
                                Summary ready
                              </span>
                            )}
                            {isFailed && (
                              <span className="dv-past-status dv-past-status--failed">
                                <i className="ph ph-warning-circle-fill" />
                                Check failed
                              </span>
                            )}
                          </td>

                          {/* ACTIONS */}
                          <td className="dv-past-cell-actions">
                            {isChecking && (
                              <div className="dv-past-actions-checking">
                                <span className="dv-past-checking-hint">
                                  Summary available when the check finishes
                                </span>
                                <button
                                  type="button"
                                  className="dv-past-trash-btn"
                                  title="Delete verification"
                                  onClick={() => setSelectedForDelete(v)}
                                >
                                  <i className="ph ph-trash" />
                                </button>
                              </div>
                            )}

                            {isReady && (
                              <div className="dv-past-actions-ready">
                                <button
                                  type="button"
                                  className="dv-past-action-pill"
                                  onClick={() => handleOpenSummary(v.verificationId)}
                                >
                                  <i className="ph ph-eye" />
                                  <span>View summary</span>
                                </button>
                                <button
                                  type="button"
                                  className="dv-past-action-pill"
                                  onClick={() => handleDownloadPdf(v.verificationId)}
                                >
                                  <i className="ph ph-download-simple" />
                                  <span>PDF</span>
                                </button>
                                <button
                                  type="button"
                                  className="dv-past-trash-btn"
                                  title="Delete verification"
                                  onClick={() => setSelectedForDelete(v)}
                                >
                                  <i className="ph ph-trash" />
                                </button>
                              </div>
                            )}

                            {isFailed && (
                              <div className="dv-past-actions-ready">
                                <button
                                  type="button"
                                  className="dv-past-trash-btn"
                                  title="Delete verification"
                                  onClick={() => setSelectedForDelete(v)}
                                >
                                  <i className="ph ph-trash" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        <tr className="dv-past-ai-row">
                          <td colSpan={6}>
                            <div className={`dv-past-ai-card dv-past-ai-card--${aiPreview.state}`}>
                              <div className="dv-past-ai-main">
                                <div className="dv-past-ai-kicker">
                                  <i className="ph ph-sparkle" />
                                  <span>{aiPreview.title}</span>
                                </div>
                                <p className="dv-past-ai-summary">{aiPreview.summary}</p>
                                <div className="dv-past-ai-meta">
                                  <span>Decision: <strong>{aiPreview.decision}</strong></span>
                                  <span>Confidence: <strong>{aiPreview.confidence}</strong></span>
                                  <span>Generated: <strong>{v.aiProcessedAt ? formatGeneratedDate(v.aiProcessedAt).date : "Not ready"}</strong></span>
                                </div>
                              </div>

                              <div className="dv-past-ai-lists">
                                <div className="dv-past-ai-list">
                                  <span className="dv-past-ai-list__title">
                                    <i className="ph ph-check-circle" />
                                    Verified clearly
                                  </span>
                                  {(aiPreview.verified.length ? aiPreview.verified : ["Waiting for AI generated findings."]).map((item) => (
                                    <span className="dv-past-ai-chip dv-past-ai-chip--good" key={item}>{item}</span>
                                  ))}
                                </div>
                                <div className="dv-past-ai-list">
                                  <span className="dv-past-ai-list__title">
                                    <i className="ph ph-warning-circle" />
                                    Flagged or risks
                                  </span>
                                  {(aiPreview.flagged.length ? aiPreview.flagged : ["No flagged AI items returned yet."]).map((item) => (
                                    <span className="dv-past-ai-chip dv-past-ai-chip--warn" key={item}>{item}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* How these are stored notice banner */}
        <div className="dv-past-storage-card">
          <div className="dv-past-storage-icon">
            <i className="ph ph-lock-key" />
          </div>
          <div className="dv-past-storage-content">
            <div className="dv-past-storage-title">How these are stored</div>
            <p className="dv-past-storage-text">
              Each summary, its PDF, and the documents uploaded for it are stored encrypted and
              visible only to you. Deleting a verification permanently removes all three.
              Retention period: to be confirmed before launch.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal (PDF Screen 5 - Delete Confirmation) */}
      {selectedForDelete && (
        <div className="dv-modal-backdrop" onClick={() => !isDeleting && setSelectedForDelete(null)}>
          <div
            className="dv-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="dv-modal-trash-icon">
              <i className="ph ph-trash" />
            </div>

            <h2 className="dv-modal-title">Delete this verification?</h2>

            <p className="dv-modal-desc">
              This permanently deletes the summary for{" "}
              <strong>{selectedForDelete.applicantName || "this applicant"}</strong>, its PDF,
              and the{" "}
              <strong>
                {countVerificationDocuments(selectedForDelete.documents)}{" "}
                {countVerificationDocuments(selectedForDelete.documents) === 1
                  ? "document"
                  : "documents"}
              </strong>{" "}
              uploaded for it. This cannot be undone.
            </p>

            {deleteError && (
              <div className="dv-modal-error">
                <i className="ph ph-warning" />
                <span>{deleteError}</span>
              </div>
            )}

            <div className="dv-modal-actions">
              <button
                type="button"
                className="dv-modal-btn dv-modal-btn--cancel"
                onClick={() => setSelectedForDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dv-modal-btn dv-modal-btn--danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="ph ph-circle-notch dv-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete permanently"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVerificationPast;
